import {
  AlertCircle,
  Bell,
  BookOpen,
  Database,
  FileCode,
  FileText,
  Footprints,
  GitCommit,
  Globe,
  Hammer,
  Hash,
  LayoutDashboard,
  LineChart,
  MonitorDot,
  Rocket,
  Terminal,
  TreePine,
  TriangleAlert,
  Workflow,
  Zap} from "lucide-react";
import {
  DatadogDashboardQuery,
  DatadogEventQuery,
  DatadogLogQuery,
  DatadogNotebookQuery,
  DatadogRUMAnalyticsAggregateQuery,
  DatadogSpanQuery,
  DatadogTimeseriesMetricQuery,
  EventType,
  FetchWebContentQuery,
  GrafanaCloudLogQuery,
  GrafanaCloudSpanQuery,
  GrafanaCloudTimeseriesMetricQuery,
  NewRelicDashboardQuery,
  OpenSearchLogQuery,
  PrometheusRangeQuery,
  RootlyDashboardQuery,
  SearchWebQuery,
} from "src/types/investigation";

export type ArtifactReference = { artifactId: string, groupTags: string[], query: string };
export type ArtifactDataTableType = Array<[any[]]>;
type ArtifactContainer = DatadogDashboardQuery | DatadogNotebookQuery | DatadogEventQuery | DatadogSpanQuery | DatadogRUMAnalyticsAggregateQuery | PrometheusRangeQuery | OpenSearchLogQuery | DatadogRUMAnalyticsAggregateQuery | DatadogLogQuery | DatadogTimeseriesMetricQuery | GrafanaCloudTimeseriesMetricQuery | GrafanaCloudLogQuery | GrafanaCloudSpanQuery | NewRelicDashboardQuery | RootlyDashboardQuery | SearchWebQuery | FetchWebContentQuery;

/**
 * Returns the integration icon type based on the provided type name
 * @param type The source type name (e.g., "DatadogLogQuery")
 * @returns The integration type to use with IntegrationIcon component
 */
const getIntegrationIconType = (type: string): string => {
  const typeMap: Record<string, string> = {
    // Datadog
    "DatadogDashboardQuery": "DATADOG",
    "DatadogNotebookQuery": "DATADOG",
    "DatadogLogQuery": "DATADOG",
    "DatadogEventQuery": "DATADOG",
    "DatadogTimeseriesMetricQuery": "DATADOG",
    "DatadogSpanQuery": "DATADOG",
    "DatadogRUMAnalyticsAggregateQuery": "DATADOG",

    // AWS
    "CloudWatchLogsQuery": "CLOUDWATCH",
    "CloudWatchMetricsQuery": "CLOUDWATCH",

    // Prometheus
    "PrometheusRangeQuery": "PROMETHEUS",
    "PrometheusInstantQuery": "PROMETHEUS",

    // OpenSearch
    "OpenSearchLogQuery": "OPENSEARCH",

    // Grafana
    "GrafanaCloudLogQuery": "GRAFANA_CLOUD",
    "GrafanaCloudSpanQuery": "GRAFANA_CLOUD",
    "GrafanaCloudTimeseriesMetricQuery": "GRAFANA_CLOUD",

    // New Relic
    "NewRelicDashboardQuery": "NEW_RELIC",
    "NewRelicNrqlQuery": "NEW_RELIC",
    "NewRelicChangeTrackingQuery": "NEW_RELIC",

    // Buildkite
    "BuildkiteAnnotationQuery": "BUILDKITE",
    "BuildkiteArtifactQuery": "BUILDKITE",
    "BuildkiteBuildQuery": "BUILDKITE",
    "BuildkiteFlakyTestQuery": "BUILDKITE",
    "BuildkiteLogQuery": "BUILDKITE",

    // Splunk
    "SplunkQuery": "SPLUNK",

    // Github
    "GitHubActionsRunQuery": "GITHUB",
    "GitHubActionsLogsQuery": "GITHUB",
    "GithubQuery": "GITHUB",

    // Dynatrace
    "DynatraceQuery": "DYNATRACE",

    // Sentry
    "SentryQuery": "SENTRY",

    // Rootly
    "RootlyDashboardQuery": "ROOTLY",

    // GCP Cloud Monitoring
    "GCPCloudMonitoringQuery": "GCP_CLOUD_MONITORING",
    "GCPCloudMonitoringLogQuery": "GCP_CLOUD_MONITORING",
    "GCPCloudMonitoringMetricQuery": "GCP_CLOUD_MONITORING",
    "GCPCloudMonitoringLogBucketsQuery": "GCP_CLOUD_MONITORING",
    "GCPCloudMonitoringLogsAggregationQuery": "GCP_CLOUD_MONITORING",

    // AppDynamics
    "AppDynamicsQuery": "APPDYNAMICS",

    // Elasticsearch
    "ElasticQuery": "ELASTICSEARCH",
    "ElasticsearchLogQuery": "ELASTICSEARCH",
    "ElasticsearchAggregationQuery": "ELASTICSEARCH",
    "ElasticsearchIndicesQuery": "ELASTICSEARCH",

    // Web Search (default tools)
    "SearchWebQuery": "default",
    "FetchWebContentQuery": "default",
  };

  return typeMap[type] || "default";
};

// Unified type definition for evidence/artifact types styling
export interface ArtifactTypeStyle {
  // Display name
  label: string;
  // Icon component
  icon: any;
  // Text color for labels and icons
  textColor: string;
  // Background color for badges
  bgColor: string;
}

const getArtifactReferenceFromEvent = (event: EventType): ArtifactReference | null => {
  if (event.prometheus_range_query) {
    return getArtifactReference(event.prometheus_range_query);
  }
  if (event.opensearch_log_query) {
    return getArtifactReference(event.opensearch_log_query);
  }
  if (event.datadog_log_query) {
    return getArtifactReference(event.datadog_log_query);
  }
  if (event.datadog_timeseries_metric_query) {
    return getArtifactReference(event.datadog_timeseries_metric_query);
  }
  if (event.grafana_cloud_timeseries_metric_query) {
    return getArtifactReference(event.grafana_cloud_timeseries_metric_query);
  }
  if (event.grafana_cloud_log_query) {
    return getArtifactReference(event.grafana_cloud_log_query);
  }
  if (event.datadog_rum_analytics_aggregate_query) {
    return getArtifactReference(event.datadog_rum_analytics_aggregate_query);
  }
  if (event.datadog_span_analytics_aggregate_query) {
    return getArtifactReference(event.datadog_span_analytics_aggregate_query);
  }
  if (event.datadog_event_query) {
    return getArtifactReference(event.datadog_event_query);
  }
  if (event.rootly_dashboard_query) {
    return getArtifactReference(event.rootly_dashboard_query);
  }
  return null;
};

const getArtifactReference = (item: ArtifactContainer): ArtifactReference | null => {
  if (!item.artifact_id) return null;

  let groupTags: string[] = [];
  if ("group_tags" in item) {
    groupTags = item.group_tags || [];
  }

  // For DatadogDashboardQuery, use dashboard_id as the query string
  if ("dashboard_id" in item && !("dashboard_slug" in item)) {
    const ddDashboard = item as DatadogDashboardQuery;
    return { artifactId: item.artifact_id, groupTags, query: ddDashboard.dashboard_id };
  }

  // For DatadogNotebookQuery, use notebook_id as the query string
  if ("notebook_id" in item) {
    const ddNotebook = item as DatadogNotebookQuery;
    return { artifactId: item.artifact_id, groupTags, query: ddNotebook.notebook_id };
  }

  // For RootlyDashboardQuery, use dashboard_slug or dashboard_id as the query string
  let query = "query" in item ? item.query : "";
  if ("dashboard_slug" in item || "dashboard_id" in item) {
    const rootlyItem = item as RootlyDashboardQuery;
    query = rootlyItem.dashboard_slug || rootlyItem.dashboard_id || "";
  }

  // For SentryIssuesQuery, use query_params as the query string
  if ("query_params" in item && "project_id" in item) {
    const sentryItem = item as any;
    query = sentryItem.query_params || "";
  }

  // For SearchWebQuery, use query as the query string
  if ("query" in item) {
    const searchWebItem = item as SearchWebQuery;
    query = searchWebItem.query || "";
  }

  // For FetchWebContentQuery, show URL count or first URL
  if ("urls" in item) {
    const fetchWebItem = item as FetchWebContentQuery;
    if (fetchWebItem.urls && fetchWebItem.urls.length > 0) {
      query = fetchWebItem.urls.length === 1 ? fetchWebItem.urls[0] : `${fetchWebItem.urls.length} URLs`;
    }
  }

  return { artifactId: item.artifact_id, groupTags, query };
};


const getArtifactTypeStyle = (type: string): ArtifactTypeStyle => {
  const lowerType = type.toLowerCase();
  const styleMap: Record<string, ArtifactTypeStyle> = {
    logs: {
      label: "Logs",
      icon: TreePine,
      textColor: "text-amber-600",
      bgColor: "bg-amber-600/30",
    },
    metrics: {
      label: "Metrics",
      icon: LineChart,
      textColor: "text-green-500",
      bgColor: "bg-green-500/30",
    },
    traces: {
      label: "Traces",
      icon: Footprints,
      textColor: "text-blue-500",
      bgColor: "bg-blue-500/30",
    },
    anomaly: {
      label: "Anomaly",
      icon: Zap,
      textColor: "text-red-400",
      bgColor: "bg-red-400/30",
    },
    alert: {
      label: "Alert",
      icon: Bell,
      textColor: "text-red-500",
      bgColor: "bg-red-500/30",
    },
    info: {
      label: "Info",
      icon: BookOpen,
      textColor: "text-muted-foreground",
      bgColor: "bg-slate-400/30",
    },
    deployment: {
      label: "Deployment",
      icon: Rocket,
      textColor: "text-purple-500",
      bgColor: "bg-purple-500/30",
    },
    rum: {
      label: "RUM",
      icon: AlertCircle,
      textColor: "text-amber-600",
      bgColor: "bg-amber-600/30",
    },
    commits: {
      label: "Commits",
      icon: GitCommit,
      textColor: "text-blue-500",
      bgColor: "bg-blue-500/30",
    },
    build_logs: {
      label: "Build Logs",
      icon: Rocket,
      textColor: "text-purple-500",
      bgColor: "bg-purple-500/30",
    },
    annotation: {
      label: "Annotations",
      icon: FileCode,
      textColor: "text-muted-foreground",
      bgColor: "bg-slate-400/30",
    },
    events: {
      label: "Events",
      icon: Zap,
      textColor: "text-yellow-500",
      bgColor: "bg-yellow-500/30",
    },
    dashboards: {
      label: "Dashboards",
      icon: LayoutDashboard,
      textColor: "text-sky-400",
      bgColor: "bg-sky-400/30",
    },
    builds: {
      label: "Builds",
      icon: Hammer,
      textColor: "text-amber-400",
      bgColor: "bg-amber-400/30",
    },
    workflows: {
      label: "Workflows",
      icon: Workflow,
      textColor: "text-violet-400",
      bgColor: "bg-violet-400/30",
    },
    monitors: {
      label: "Monitors",
      icon: MonitorDot,
      textColor: "text-pink-500",
      bgColor: "bg-pink-500/30",
    },
    issues: {
      label: "Issues",
      icon: TriangleAlert,
      textColor: "text-red-400",
      bgColor: "bg-red-400/30",
    },
    spans: {
      label: "Spans",
      icon: Footprints,
      textColor: "text-teal-500",
      bgColor: "bg-teal-500/30",
    },
    memories: {
      label: "Memories",
      icon: BookOpen,
      textColor: "text-indigo-400",
      bgColor: "bg-indigo-400/30",
    },
    documents: {
      label: "Documents",
      icon: FileText,
      textColor: "text-primary",
      bgColor: "bg-blue-400/30",
    },
    web: {
      label: "Web",
      icon: Globe,
      textColor: "text-sky-400",
      bgColor: "bg-sky-400/30",
    },
    data: {
      label: "Data",
      icon: Database,
      textColor: "text-muted-foreground",
      bgColor: "bg-slate-500/30",
    },
    channel: {
      label: "Channel",
      icon: Hash,
      textColor: "text-purple-400",
      bgColor: "bg-purple-400/30",
    },
    threads: {
      label: "Threads",
      icon: Workflow,
      textColor: "text-cyan-400",
      bgColor: "bg-cyan-400/30",
    },
    cli_output: {
      label: "CLI Output",
      icon: Terminal,
      textColor: "text-green-400",
      bgColor: "bg-green-400/30",
    },
  };

  return styleMap[lowerType] || {
    label: "Data",
    icon: BookOpen,
    textColor: "text-muted-foreground",
    bgColor: "bg-slate-400/30",
  };
};

export { getArtifactReference, getArtifactReferenceFromEvent, getArtifactTypeStyle, getIntegrationIconType };
